import {
  createRoom,
  describeRoomError,
  findTimeControl,
  materialize,
  replay,
  timestamp,
  type Actor,
  type ClientMessage,
  type Command,
  type RoomId,
  type RoomState,
  type ServerMessage,
} from '@chess/core'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { ConnectionStatus, RoomConnection } from './connection.js'

const RECONNECT_MIN_MS = 500
const RECONNECT_MAX_MS = 8_000

const socketUrl = (roomId: string, playerKey: string | null): string => {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  const key = playerKey === null ? '' : `?k=${encodeURIComponent(playerKey)}`
  return `${protocol}//${window.location.host}/api/rooms/${encodeURIComponent(roomId)}/socket${key}`
}

const emptyRoom = (roomId: string): RoomState =>
  createRoom(roomId as RoomId, findTimeControl('10+0')?.control ?? { kind: 'unlimited' }, timestamp(Date.now()))

/**
 * A room driven by the Durable Object.
 *
 * The connection is deliberately dumb: on open the server sends the whole event
 * log and the client folds it with the same reducer the server uses, so
 * reconnecting needs no delta protocol, no sequence numbers, and no
 * reconciliation — just another snapshot.
 */
export const useRemoteRoom = (roomId: string, playerKey: string | null, name: string): RoomConnection => {
  const [state, setState] = useState<RoomState>(() => emptyRoom(roomId))
  const [actor, setActor] = useState<Actor>({ kind: 'spectator' })
  const [status, setStatus] = useState<ConnectionStatus>('connecting')
  const [present, setPresent] = useState<readonly [boolean, boolean]>([false, false])
  const [lastError, setLastError] = useState<string | null>(null)
  const [ready, setReady] = useState(false)

  /** serverTime - clientTime, so countdowns agree with the authority. */
  const [skewMs, setSkewMs] = useState(0)

  const socketRef = useRef<WebSocket | null>(null)
  const attemptRef = useRef(0)
  const nameRef = useRef(name)
  nameRef.current = name

  useEffect(() => {
    let disposed = false
    let retryTimer = 0

    const connect = (): void => {
      if (disposed) return
      setStatus((current) => (current === 'open' ? 'connecting' : current))

      const socket = new WebSocket(socketUrl(roomId, playerKey))
      socketRef.current = socket

      socket.addEventListener('open', () => {
        attemptRef.current = 0
        setStatus('open')
      })

      socket.addEventListener('message', (event) => {
        let message: ServerMessage
        try {
          message = JSON.parse(String(event.data)) as ServerMessage
        } catch {
          return
        }

        switch (message.type) {
          case 'snapshot': {
            setSkewMs(message.serverTimeMs - Date.now())
            setActor(message.you)
            setPresent(message.present)
            setState(materialize(message.snapshot))
            setReady(true)
            // Announce ourselves; the server ignores it for spectators.
            if (message.you.kind === 'player') {
              socket.send(
                JSON.stringify({
                  type: 'command',
                  command: { type: 'arrive', name: nameRef.current },
                } satisfies ClientMessage),
              )
            }
            return
          }
          case 'events':
            setState((current) => replay(current, message.events))
            return
          case 'present':
            setPresent(message.present)
            return
          case 'rejected':
            setLastError(describeRoomError(message.error))
            return
        }
      })

      socket.addEventListener('close', () => {
        if (disposed) return
        setStatus('connecting')
        attemptRef.current += 1
        const delay = Math.min(RECONNECT_MAX_MS, RECONNECT_MIN_MS * 2 ** (attemptRef.current - 1))
        retryTimer = window.setTimeout(connect, delay)
      })

      socket.addEventListener('error', () => socket.close())
    }

    connect()

    return () => {
      disposed = true
      window.clearTimeout(retryTimer)
      socketRef.current?.close()
      socketRef.current = null
    }
  }, [playerKey, roomId])

  const send = useCallback((command: Command): void => {
    const socket = socketRef.current
    if (socket === null || socket.readyState !== WebSocket.OPEN) return
    socket.send(JSON.stringify({ type: 'command', command } satisfies ClientMessage))
  }, [])

  // The server owns the clock; nothing here can end a game, but asking keeps
  // the local view honest if our own connection is the one that woke up late.
  useEffect(() => {
    const id = window.setInterval(() => send({ type: 'tick' }), 1_000)
    return () => window.clearInterval(id)
  }, [send])

  const opponentOnline =
    actor.kind === 'player' ? (present[actor.slot === 0 ? 1 : 0] ?? false) : present[0] && present[1]

  return {
    state,
    actor,
    status,
    opponentOnline,
    send,
    lastError,
    clearError: useCallback(() => setLastError(null), []),
    nowOffsetMs: skewMs,
    ready,
  }
}
